import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BaseDomainType,
  CommodityDataGridColumnGroup,
  CommodityDataGridField,
  CommodityDataGridRow,
} from '~/common/types';
import {
  cellFactory,
  ChipCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  ListCountCell,
  numberFormatterFactory,
  TooltipCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { commodityGroupValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';
import { ListCountCellMenu } from '~/components/ListCountCellMenu/ListCountCellMenu';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';

export const useCommodityDataGridConfig: UseDataGridConfig<CommodityDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.commodity.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.commodity.columnBands' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      // sortModel: [{ field: CommodityDataGridField.NAME, sort: GridSortDirectionType.ASC }],
      columnVisibilityModel: {
        [CommodityDataGridField.DELIVERY_ON_TIME_PERCENTAGE]: false,
        [CommodityDataGridField.CORRECT_ORDER_PERCENTAGE]: false,
        [CommodityDataGridField.DEVIATING_PRICE_PERCENTAGE]: false,
        [CommodityDataGridField.DEVIATING_QUANTITY_PERCENTAGE]: false,
        [CommodityDataGridField.LEAD_TIME]: false,
      },
      columns: [
        createColumn({
          field: CommodityDataGridField.EXTERNAL_ID,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.commodityDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          hideable: false,
        }),
        createColumn({
          field: CommodityDataGridField.NAME,
          renderCell: cellFactory(TooltipCell),
          minWidth: 350,
        }),
        createColumn({
          field: CommodityDataGridField.DESCRIPTION,
          renderCell: cellFactory(TooltipCell),
          minWidth: 350,
        }),
        createColumn({
          field: CommodityDataGridField.SUPPLIER_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.supplierList) &&
              (() => (
                <ListCountCellMenu
                  list={row.supplierList}
                  linkAll={{
                    label: 'Base.commodity.links.supplier',
                    pathname: RouteId.analyticsBase(BaseDomainType.SUPPLIER),
                    rules: [],
                  }}
                />
              )),
          })),
          minWidth: 140,
        }),
        createColumn({
          field: CommodityDataGridField.CONTRACT_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.contractList) &&
              (() => (
                <ListCountCellMenu
                  list={row.contractList}
                  linkAll={{
                    label: 'Base.commodity.links.contract',
                    pathname: RouteId.analyticsBase(BaseDomainType.CONTRACT),
                    rules: [],
                  }}
                />
              )),
          })),
          minWidth: 140,
        }),
        createColumn({
          field: CommodityDataGridField.DELIVERY_ON_TIME_PERCENTAGE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          align: GridAlignmentType.RIGHT,
          minWidth: 150,
        }),
        createColumn({
          field: CommodityDataGridField.CORRECT_ORDER_PERCENTAGE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          align: GridAlignmentType.RIGHT,
          minWidth: 150,
        }),
        createColumn({
          field: CommodityDataGridField.DEVIATING_PRICE_PERCENTAGE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          align: GridAlignmentType.RIGHT,
          minWidth: 150,
        }),
        createColumn({
          field: CommodityDataGridField.DEVIATING_QUANTITY_PERCENTAGE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          align: GridAlignmentType.RIGHT,
          minWidth: 150,
        }),
        createColumn({
          field: CommodityDataGridField.LEAD_TIME,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 100,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityDataGridField.ORDER_VOLUME_YEAR,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityDataGridField.ORDER_COUNT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityDataGridField.ORDER_VOLUME,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityDataGridField.PRIMARY_COMMODITY_GROUP,
          renderCell: cellFactory(ChipCell, ({ value }) => ({ value: commodityGroupValueGetter(value) })),
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: CommodityDataGridField.COMMODITY_GROUP_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.commodityGroupList) && (() => <ListCountCellMenu list={row.commodityGroupList} />),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: CommodityDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          minWidth: 120,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: CommodityDataGridColumnGroup.COMMODITY,
          renderHeaderGroup: headerGroupWithIcon(IconId.commodity),
          children: [
            //
            { field: CommodityDataGridField.EXTERNAL_ID },
            { field: CommodityDataGridField.NAME },
            { field: CommodityDataGridField.DESCRIPTION },
            { field: CommodityDataGridField.PRIMARY_COMMODITY_GROUP },
            { field: CommodityDataGridField.COMMODITY_GROUP_COUNT },
          ],
        }),
        createGroup({
          groupId: CommodityDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: CommodityDataGridField.SUPPLIER_COUNT },
          ],
        }),
        createGroup({
          groupId: CommodityDataGridColumnGroup.CONTRACT,
          renderHeaderGroup: headerGroupWithIcon(IconId.contract),
          children: [
            //
            { field: CommodityDataGridField.CONTRACT_COUNT },
          ],
        }),
        createGroup({
          groupId: CommodityDataGridColumnGroup.PERFORMANCE,
          renderHeaderGroup: headerGroupWithIcon(IconId.Speed),
          children: [
            //
            { field: CommodityDataGridField.DELIVERY_ON_TIME_PERCENTAGE },
            { field: CommodityDataGridField.CORRECT_ORDER_PERCENTAGE },
            { field: CommodityDataGridField.DEVIATING_PRICE_PERCENTAGE },
            { field: CommodityDataGridField.DEVIATING_QUANTITY_PERCENTAGE },
            { field: CommodityDataGridField.LEAD_TIME },
          ],
        }),
        createGroup({
          groupId: CommodityDataGridColumnGroup.ORDER,
          renderHeaderGroup: headerGroupWithIcon(IconId.order),
          children: [
            //
            { field: CommodityDataGridField.ORDER_COUNT },
            { field: CommodityDataGridField.ORDER_VOLUME_YEAR },
            { field: CommodityDataGridField.ORDER_COUNT_YEAR },
            { field: CommodityDataGridField.ORDER_VOLUME },
          ],
        }),
      ],
      exportModel: {
        [CommodityDataGridField.DELIVERY_ON_TIME_PERCENTAGE]: {
          numFmt: '0.00%',
        },
        [CommodityDataGridField.CORRECT_ORDER_PERCENTAGE]: {
          numFmt: '0.00%',
        },
        [CommodityDataGridField.DEVIATING_PRICE_PERCENTAGE]: {
          numFmt: '0.00%',
        },
        [CommodityDataGridField.DEVIATING_QUANTITY_PERCENTAGE]: {
          numFmt: '0.00%',
        },
        [CommodityDataGridField.LINKS]: {
          disable: true,
        },
      },
    } as DataGridConfig<CommodityDataGridRow>;
  }, [t, tColumn, tGroup]);
};
